import { reportError } from "@/utils/reportError";
import PrimaryButton from "@/components/ui/PrimaryButton";
import { createSessionFromUrl } from "@/utils/authSession";
import { supabase } from "@/utils/supabase";
import { showAlert } from "@/utils/showAlert";
import { translateSupabaseError } from "@/utils/supabaseErrorTranslations";
import { validatePassword } from "@/utils/validationUtils";
import { Ionicons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import * as Linking from "expo-linking";
import { useRouter } from "expo-router";
import { StatusBar } from "expo-status-bar";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActivityIndicator,
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    Text,
    TextInput,
    TouchableOpacity,
    View,
} from "react-native";

export default function ResetPasswordScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmError, setConfirmError] = useState("");

  // URL du deep link de récupération (repas://reset-password?code=...). Le
  // token du lien email n'était auparavant JAMAIS consommé : l'écran se
  // contentait de lire la session locale, donc un utilisateur déconnecté
  // voyait toujours « Lien expiré » et un appareil avec session active
  // pouvait changer le mot de passe sans preuve de possession du lien.
  const url = Linking.useURL();
  const expiredAlertShownRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      let sessionFromLink = false;
      if (url) {
        try {
          sessionFromLink = await createSessionFromUrl(url);
        } catch (err) {
          reportError(err);
        }
      }

      const { data: { session } } = await supabase.auth.getSession();
      if (cancelled || session) return;

      // Pas de session : si on a déjà tenté de consommer l'URL (ou qu'aucune
      // URL n'arrivera — navigation interne), le lien est invalide/expiré.
      if (expiredAlertShownRef.current) return;
      if (!url && !sessionFromLink) {
        // Laisse une chance à l'URL initiale du deep link d'arriver.
        return;
      }
      expiredAlertShownRef.current = true;
      showAlert(
        t("resetPassword.linkExpiredTitle"),
        t("resetPassword.linkExpiredMessage"),
        [{ text: t("resetPassword.backToLogin"), onPress: () => router.replace("/login") }]
      );
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url]);

  const handleResetPassword = async () => {
    // Reset errors
    setPasswordError("");
    setConfirmError("");

    // Validate password
    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.error || "");
      return;
    }

    // Validate confirmation
    if (password !== confirmPassword) {
      setConfirmError(t("resetPassword.passwordMismatch"));
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        const translatedError = translateSupabaseError(error);
        showAlert(translatedError.title, translatedError.message);
        return;
      }

      showAlert(
        t("resetPassword.successTitle"),
        t("resetPassword.successMessage"),
        [
          {
            text: t("common.continue"),
            onPress: () => router.replace("/(tabs)"),
          },
        ]
      );
    } catch (error: any) {
      reportError(error);
      const translatedError = translateSupabaseError(error);
      showAlert(translatedError.title, translatedError.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      style={styles.container}
    >
      <StatusBar style="dark" />
      <LinearGradient
        colors={["#FBEFE3", "#FFFFFF"]}
        locations={[0, 0.65]}
        style={styles.background}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          showsVerticalScrollIndicator={false}
        >
          <View style={styles.header}>
            <Ionicons name="lock-closed" size={48} color="#1C453F" />
            <Text style={styles.title}>{t("resetPassword.title")}</Text>
            <Text style={styles.subtitle}>{t("resetPassword.subtitle")}</Text>
          </View>

          <View style={styles.formContainer}>
            <View
              style={[
                styles.inputContainer,
                passwordError && styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("resetPassword.newPasswordPlaceholder")}
                placeholderTextColor="#999"
                style={[styles.input, { flex: 1 }]}
                secureTextEntry={!showPassword}
                value={password}
                onChangeText={(text) => {
                  setPassword(text);
                  setPasswordError("");
                }}
                editable={!isLoading}
              />
              <TouchableOpacity onPress={() => setShowPassword(!showPassword)}>
                <Ionicons
                  name={showPassword ? "eye-outline" : "eye-off-outline"}
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            {passwordError ? (
              <Text style={styles.errorText}>{passwordError}</Text>
            ) : null}
          </View>

          <View style={styles.formContainer}>
            <View
              style={[
                styles.inputContainer,
                confirmError && styles.inputError,
              ]}
            >
              <TextInput
                placeholder={t("resetPassword.confirmPasswordPlaceholder")}
                placeholderTextColor="#999"
                style={[styles.input, { flex: 1 }]}
                secureTextEntry={!showConfirmPassword}
                value={confirmPassword}
                onChangeText={(text) => {
                  setConfirmPassword(text);
                  setConfirmError("");
                }}
                editable={!isLoading}
              />
              <TouchableOpacity
                onPress={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                <Ionicons
                  name={
                    showConfirmPassword ? "eye-outline" : "eye-off-outline"
                  }
                  size={20}
                  color="#999"
                />
              </TouchableOpacity>
            </View>
            {confirmError ? (
              <Text style={styles.errorText}>{confirmError}</Text>
            ) : null}
          </View>

          <View style={styles.requirementsContainer}>
            <Text style={styles.requirementsTitle}>
              {t("resetPassword.requirementsTitle")}
            </Text>
            <Text style={styles.requirementItem}>{t("resetPassword.requirementMinLength")}</Text>
            <Text style={styles.requirementItem}>{t("resetPassword.requirementUppercase")}</Text>
            <Text style={styles.requirementItem}>{t("resetPassword.requirementLowercase")}</Text>
            <Text style={styles.requirementItem}>{t("resetPassword.requirementDigit")}</Text>
          </View>

          <PrimaryButton
            title={
              isLoading ? (
                <ActivityIndicator color="#FFF" />
              ) : (
                t("resetPassword.submitButton")
              )
            }
            onPress={handleResetPassword}
            disabled={isLoading}
          />

          <TouchableOpacity
            onPress={() => router.replace("/login")}
            style={styles.backButton}
          >
            <Text style={styles.backButtonText}>{t("resetPassword.backToLogin")}</Text>
          </TouchableOpacity>
        </ScrollView>
      </LinearGradient>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  background: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 24,
    paddingTop: 80,
    paddingBottom: 40,
  },
  header: {
    alignItems: "center",
    marginBottom: 40,
  },
  title: {
    fontFamily: "Inter_700Bold",
    fontSize: 28,
    color: "#000",
    marginTop: 16,
    marginBottom: 8,
  },
  subtitle: {
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#666",
    textAlign: "center",
  },
  formContainer: {
    marginBottom: 16,
  },
  inputContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 56,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 4,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 2,
    borderWidth: 1,
    borderColor: "transparent",
  },
  inputError: {
    borderColor: "#D32F2F",
  },
  input: {
    flex: 1,
    fontFamily: "Inter_400Regular",
    fontSize: 16,
    color: "#000",
    height: "100%",
  },
  errorText: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#D32F2F",
    marginTop: 4,
    marginLeft: 4,
  },
  requirementsContainer: {
    backgroundColor: "#FFF9F0",
    borderRadius: 12,
    padding: 16,
    marginBottom: 24,
    borderLeftWidth: 3,
    borderLeftColor: "#733801",
  },
  requirementsTitle: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#733801",
    marginBottom: 8,
  },
  requirementItem: {
    fontFamily: "Inter_400Regular",
    fontSize: 13,
    color: "#666",
    marginBottom: 4,
  },
  backButton: {
    marginTop: 16,
    alignItems: "center",
  },
  backButtonText: {
    fontFamily: "Inter_600SemiBold",
    fontSize: 14,
    color: "#8B4513",
  },
});
